import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoPiscis = () => {
  const articleSchema = createArticleSchema(
    "Piscis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 19 de febrero y el 20 de marzo.",
    `${window.location.origin}/horoscopo-piscis`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Piscis",
        "item": `${window.location.origin}/horoscopo-piscis`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Piscis" }
  ];

  return (
    <>
      <Helmet>
        <title>Piscis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 19 de febrero y el 20 de marzo." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-piscis`} />
        <meta property="og:title" content="Piscis: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-piscis`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♓</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Piscis
              </h1>
              <p className="text-xl text-muted-foreground">19 de Febrero - 20 de Marzo</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Piscis: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="piscis" signName="Piscis" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Piscis: El Soñador Místico</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Piscis</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💧 Elemento: Agua</h4>
                    <p className="text-muted-foreground">Enfoque en la espiritualidad, la intuición y la disolución del ego. Piscis fluye con el océano cósmico.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🌊 Modalidad: Mutable</h4>
                    <p className="text-muted-foreground">Define la adaptabilidad infinita, la trascendencia y la compasión universal.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♆♃ Planetas Regentes</h4>
                    <p className="text-muted-foreground">Neptuno (Misticismo) y Júpiter (Expansión). Sueños infinitos y fe trascendente.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Piscis: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Compasión:</strong> Empatía universal sin límites</li>
                      <li>• <strong>Intuición:</strong> Conexión psíquica profunda</li>
                      <li>• <strong>Creatividad:</strong> Imaginación sin fronteras</li>
                      <li>• <strong>Espiritualidad:</strong> Conexión con lo divino</li>
                      <li>• <strong>Adaptabilidad:</strong> Fluye con cualquier situación</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Escapismo:</strong> Huye de la realidad difícil</li>
                      <li>• <strong>Victimismo:</strong> Se sacrifica excesivamente</li>
                      <li>• <strong>Confusión:</strong> Dificultad con límites claros</li>
                      <li>• <strong>Ingenuidad:</strong> Fácilmente engañado</li>
                      <li>• <strong>Autoengaño:</strong> Evita verdades dolorosas</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Creo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Piscis en el Amor: Romance Sublime, Fusión y Sacrificio</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Piscis, el amor es una experiencia espiritual y trascendente. Busca fusión total con su pareja, una conexión que vaya más allá de lo físico hacia lo místico. Su amor es devoto, romántico, compasivo y a veces sacrificial.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una pareja que valore su sensibilidad y proteja su corazón vulnerable. Puede perderse en el otro si no mantiene límites saludables. Cuando encuentra un amor verdadero, ofrece devoción absoluta, creatividad romántica y comprensión intuitiva incomparable.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Piscis en el Trabajo y Finanzas: Artes, Sanación y Servicio</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Piscis destaca en artes, música, cine, fotografía, sanación holística, espiritualidad, trabajo social y cualquier campo que requiera compasión y creatividad. Su intuición lo hace excelente en profesiones de ayuda y trabajo con el subconsciente.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Piscis es el menos materialista del zodiaco. Puede ser generoso hasta la imprudencia y descuidado con detalles prácticos. Necesita apoyo para gestión financiera. Excelente para carreras creativas donde el dinero fluye naturalmente de su talento.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Piscis con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♋ Cáncer y ♏ Escorpio</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua comparten profundidad emocional y conexión intuitiva. Con Cáncer hay nutrición mutua y hogar compartido. Con Escorpio, intensidad transformadora y lealtad absoluta.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♉ Tauro y ♑ Capricornio</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra proporcionan el ancla que Piscis necesita. Tauro ofrece estabilidad sensorial, mientras que Capricornio aporta estructura para manifestar los sueños de Piscis en realidad.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Piscis</h3>
                <p className="text-muted-foreground mb-4">
                  Los límites y la practicidad son los grandes desafíos de Piscis. Virgo, su signo opuesto, le enseña sobre organización, discernimiento y la importancia de los detalles prácticos para manifestar visiones espirituales.
                </p>
                <p className="text-muted-foreground">
                  Los signos de fuego (Aries, Leo, Sagitario) desafían su pasividad con acción y afirmación personal. Los signos de aire (Géminis, Libra, Acuario) le enseñan sobre claridad mental y la importancia de la comunicación directa.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoPiscis;
